<!-- @component
	Displays recommendation procedures for an analysis.
	Provider component that manages stores and delegates rendering
	to RecommendationCard children.
-->
<script lang="ts">
	import Notification from '../molecules/Notification.svelte'
	import {
		analyticalMetadata,
		analyticNames,
		loadRecommendations,
		recommendationParameters,
		recommendations,
		recommendationValues,
		selectedRecommendation,
		values
	} from './stores'
	import { get } from 'svelte/store'
	import { t } from '$i18n/i18n'
	import { createEventDispatcher } from 'svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import { debounce } from '$utils/utils'
	import { AnalyticalParentType } from '$types/enums'
	import type { Recommendation } from './types/recommendation'
	import { NotificationType } from '$types/notification'
	import RecommendationCard from './RecommendationCard.svelte'

	// Keep createEventDispatcher — AnalyticsTool.svelte (Svelte 4) uses on:valuesChanged
	const dispatch = createEventDispatcher()

	const urlParams = new URLSearchParams(window.location.search)

	let { editable = true }: { editable?: boolean } = $props()

	let analyticalType = $derived($analyticalMetadata.analyticalType)
	let analyticalId = $derived($analyticalMetadata.analyticalId)
	let parentId = $derived($analyticalMetadata.parentId)
	let parentType = $derived($analyticalMetadata.parentType)

	// URL param init — runs once at mount
	if (urlParams.get('selectedRecommendation')) {
		$selectedRecommendation = urlParams.get('selectedRecommendation')
	}

	let showHint = $state(true)
	const onHintDismiss = () => {
		showHint = false
	}

	const transformParametersForRequest = (analytic: string) => {
		const parameters = []
		for (const parameterKey in $recommendationParameters[analytic]) {
			if ($recommendationParameters[analytic][parameterKey]) {
				parameters.push({
					name: parameterKey,
					value: $recommendationParameters[analytic][parameterKey]
				})
			}
		}
		return parameters
	}

	function addValues(analytic: string) {
		return () => {
			const storedValues = get($recommendationValues[analytic])
			storedValues.forEach((value) => (value.additional = true))
			fetchUtils
				.post(`/api/${analyticalType?.toLowerCase()}/addValues`, {
					analyticalId,
					parentId,
					parentType,
					values: storedValues,
					parameters: transformParametersForRequest(analytic)
				})
				.then(() => {
					dispatch('valuesChanged')
					loadRecommendations()
				})
		}
	}

	function addAnalysis(recommendation: Recommendation) {
		return () => {
			const recommendationValuesStoreForAnalytic = get(
				$recommendationValues[recommendation.analytic]
			)
			if (
				parentType === AnalyticalParentType.NONE ||
				parentType === AnalyticalParentType.CHECK ||
				parentId === '-1'
			) {
				const url = new URL(`/${analyticalType?.toLowerCase()}`, window.parent.location.href)
				url.searchParams.set('analyticTechnicalName', recommendation.analytic)
				for (const requiredParam in recommendation.requiredParameters) {
					url.searchParams.set(
						requiredParam,
						$recommendationParameters[recommendation.analytic][requiredParam]
					)
				}
				const valuesToStore = [
					...$values,
					...recommendationValuesStoreForAnalytic.filter(
						(value) => !$values.map((v) => v.key).includes(value.key)
					)
				]
				window.sessionStorage.setItem('analyticalValues', JSON.stringify(valuesToStore))
				window.open(url, '_blank')
			} else {
				fetchUtils
					.post(`/api/${analyticalType?.toLowerCase()}/addRecommendationAnalytic`, {
						analytic: recommendation.analytic,
						analyticalId,
						parentId,
						parentType,
						values: recommendationValuesStoreForAnalytic,
						parameters: transformParametersForRequest(recommendation.analytic)
					})
					.then((recommendationId: string) => {
						loadRecommendations()
						const valuesToStore = [
							...$values,
							...recommendationValuesStoreForAnalytic.filter(
								(value) => !$values.map((v) => v.key).includes(value.key)
							)
						]
						window.sessionStorage.setItem('analyticalValues', JSON.stringify(valuesToStore))
						window.parent.location.href = `/${analyticalType?.toLowerCase()}?${parentType?.toLowerCase()}${analyticalType?.toLowerCase()}=${recommendationId}`
					})
			}
		}
	}

	let evaluationResults: Record<string, { result: { description: string } }> = $state({})

	const updateEvaluationCallbacks: Record<string, () => void> = {}

	function updateEvaluation(analytic: string) {
		if (!updateEvaluationCallbacks[analytic]) {
			updateEvaluationCallbacks[analytic] = debounce(() => {
				const parameters = transformParametersForRequest(analytic)

				const recommendationValuesForAnalytic = get($recommendationValues[analytic])
				const recommendationVals = [
					...recommendationValuesForAnalytic.filter(
						(value) => !$values.map((v) => v.key).includes(value.key)
					)
				]
				fetchUtils
					.post(`/api/analytics/evaluateAnalysis`, {
						analyticTechnicalName: analytic,
						parameters: parameters,
						values: [...$values, ...recommendationVals]
					})
					.then(
						(data: {
							valueResults: Record<string, unknown>
							combinedResult: Record<string, { result: { description: string } }>
						}) => {
							for (const i in recommendationVals) {
								const value = recommendationVals[i]
								value.evaluation = data.valueResults[value.key] as typeof value.evaluation
							}
							$recommendationValues[analytic].set(recommendationVals)
							evaluationResults[analytic] = data.combinedResult[analytic]
						}
					)
			})
		}

		return updateEvaluationCallbacks[analytic]
	}

	function handleParameterChanged(analytic: string, parameter: string, data: unknown) {
		// onChange returns the full selection object, not the extracted value string
		// see docs/SVELECTE.md "onChange vs bind:value"
		const value =
			typeof data === 'object' && data !== null
				? ((data as Record<string, string>).value ?? String(data))
				: String(data ?? '')
		$recommendationParameters[analytic][parameter] = value
		updateEvaluation(analytic)()
	}

	function openRecommendation(id: string | null) {
		if (id) {
			const card = document.getElementById(id)
			card?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
		}
	}

	// re-evaluate when recommendations or values change
	$effect(() => {
		void $values
		for (const rec of $recommendations) {
			updateEvaluation(rec.analytic)()
		}
	})

	// scroll to selected recommendation when recommendations change
	$effect(() => {
		if ($recommendations.length) {
			openRecommendation($selectedRecommendation)
		}
	})
</script>

<h1>{$t('Analytics.recommendations.title')}</h1>
{#if showHint}
	<Notification
		type={NotificationType.INFO_INLINE}
		message={$t('Analytics.recommendations.hint')}
		on:dismiss={onHintDismiss}
		dismiss
	/>
{/if}
<div>
	<h2>{$t('Analytics.recommendations.procedures')}</h2>
	{#if $recommendations.length === 0}
		{$t('Analytics.recommendations.empty')}
	{/if}

	{#each $recommendations as recommendation (recommendation.analytic)}
		<RecommendationCard
			{recommendation}
			analyticName={$analyticNames[recommendation.analytic]}
			evaluationResult={evaluationResults[recommendation.analytic]}
			parameterValues={$recommendationParameters[recommendation.analytic] ?? {}}
			analysisValues={$recommendationValues[recommendation.analytic]}
			isOpen={$selectedRecommendation === recommendation.analytic}
			{editable}
			{parentType}
			{parentId}
			onParameterChanged={handleParameterChanged}
			onAddAnalysis={addAnalysis(recommendation)}
			onAddValues={addValues(recommendation.analytic)}
			onUpdateEvaluation={updateEvaluation(recommendation.analytic)}
		/>
		<div class="cardspacer"></div>
	{/each}
</div>

<style lang="scss">
	.cardspacer {
		height: 10px;
	}
</style>
